import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Shield, Key, CheckCircle2, Lock, ExternalLink, Save, Loader2, Eye, EyeOff, Copy, Settings2, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SAMLSSOPage() {
  const [samlConfig, setSamlConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCert, setShowCert] = useState(false);
  const [form, setForm] = useState({
    idp_sso_url: '',
    idp_x509_cert: '',
    sp_entity_id: window.location.origin,
  });

  useEffect(() => {
    fetchSAMLConfig();
  }, []);

  const fetchSAMLConfig = async () => {
    try {
      const token = localStorage.getItem('grc_token');
      const response = await axios.get(`${API}/api/auth/saml/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSamlConfig(response.data);
      if (response.data?.sso_url) {
        setForm(prev => ({
          ...prev,
          idp_sso_url: response.data.sso_url || '',
          sp_entity_id: response.data.sp_entity_id || window.location.origin,
        }));
      }
    } catch (error) {
      // Config endpoint may not exist yet, that's ok
      setSamlConfig({ enabled: false });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.idp_sso_url || !form.idp_x509_cert) {
      toast.error('SSO URL and X.509 Certificate are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('grc_token');
      await axios.post(`${API}/api/integrations/configure`, {
        integration_id: 'saml_sso',
        credentials: form
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('SAML SSO configuration saved');
      await fetchSAMLConfig();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save SAML config');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  const isEnabled = samlConfig?.enabled;

  return (
    <DashboardLayout>
      <div className="space-y-6 px-1" data-testid="saml-sso-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <Shield size={24} />
              SAML SSO Configuration
            </h1>
            <p className="text-sm text-slate-500 mt-1">Enterprise Single Sign-On with SAML 2.0</p>
          </div>
          <Badge className={isEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}>
            {isEnabled ? 'Enabled' : 'Not Configured'}
          </Badge>
        </div>

        {/* Status Banner */}
        <Card className={`border ${isEnabled ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-slate-50/50'}`}>
          <CardContent className="p-5 flex items-center gap-4">
            {isEnabled ? (
              <CheckCircle2 size={24} className="text-emerald-600 flex-shrink-0" />
            ) : (
              <Lock size={24} className="text-slate-400 flex-shrink-0" />
            )}
            <div>
              <p className="font-medium text-slate-900">
                {isEnabled ? 'SSO is active' : 'SSO is not configured'}
              </p>
              <p className="text-sm text-slate-500">
                {isEnabled
                  ? 'Users can authenticate using your corporate identity provider.'
                  : 'Configure your Identity Provider details below to enable SSO.'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="configure">
          <TabsList>
            <TabsTrigger value="configure" data-testid="tab-configure">
              <Settings2 size={14} className="mr-1.5" /> Configure
            </TabsTrigger>
            <TabsTrigger value="guide" data-testid="tab-guide">
              <BookOpen size={14} className="mr-1.5" /> Setup Guide
            </TabsTrigger>
          </TabsList>

          <TabsContent value="configure" className="space-y-6 mt-6">
            {/* Service Provider Info */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-base">Service Provider (SP) Details</CardTitle>
                <CardDescription>Provide these to your Identity Provider</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { label: 'ACS URL (Assertion Consumer Service)', value: `${window.location.origin}/api/auth/saml/acs` },
                  { label: 'Entity ID / Audience', value: window.location.origin },
                  { label: 'Name ID Format', value: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress' },
                ].map((field) => (
                  <div key={field.label}>
                    <Label className="text-xs text-slate-500">{field.label}</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <code className="flex-1 bg-slate-50 border border-slate-200 px-3 py-2 rounded text-xs font-mono text-slate-700 break-all">
                        {field.value}
                      </code>
                      <Button variant="ghost" size="sm" onClick={() => copyToClipboard(field.value)}>
                        <Copy size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* IdP Configuration Form */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-base">Identity Provider (IdP) Configuration</CardTitle>
                <CardDescription>Enter the details from your IdP (Okta, Azure AD, OneLogin, etc.)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <Label htmlFor="sso-url">IdP SSO URL <span className="text-red-500">*</span></Label>
                  <Input
                    id="sso-url"
                    placeholder="https://your-idp.com/sso/saml"
                    value={form.idp_sso_url}
                    onChange={(e) => setForm(prev => ({ ...prev, idp_sso_url: e.target.value }))}
                    className="mt-1.5 font-mono text-sm"
                    data-testid="saml-sso-url-input"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="x509-cert">IdP X.509 Certificate <span className="text-red-500">*</span></Label>
                    <button onClick={() => setShowCert(!showCert)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                      {showCert ? <EyeOff size={12} /> : <Eye size={12} />}
                      {showCert ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <textarea
                    id="x509-cert"
                    rows={showCert ? 6 : 2}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;MIIDp...&#10;-----END CERTIFICATE-----"
                    value={form.idp_x509_cert}
                    onChange={(e) => setForm(prev => ({ ...prev, idp_x509_cert: e.target.value }))}
                    className="mt-1.5 w-full font-mono text-xs border border-slate-200 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ fontFamily: 'monospace' }}
                    data-testid="saml-cert-input"
                  />
                </div>

                <div>
                  <Label htmlFor="sp-entity">SP Entity ID</Label>
                  <Input
                    id="sp-entity"
                    value={form.sp_entity_id}
                    onChange={(e) => setForm(prev => ({ ...prev, sp_entity_id: e.target.value }))}
                    className="mt-1.5 font-mono text-sm"
                    data-testid="saml-entity-input"
                  />
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <Button onClick={handleSave} disabled={saving} data-testid="saml-save-btn">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save Configuration
                  </Button>
                  {isEnabled && (
                    <Button variant="outline" data-testid="saml-test-btn"
                      onClick={() => window.open(`${API}/api/auth/saml/login`, '_blank')}>
                      <ExternalLink size={14} className="mr-2" />
                      Test SSO Login
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="guide" className="space-y-6 mt-6">
            <Card className="border-slate-200">
              <CardContent className="p-6 space-y-6">
                {[
                  {
                    step: '1',
                    title: 'Create a SAML Application in your IdP',
                    body: 'Go to your Identity Provider (Okta, Azure AD, OneLogin, Google Workspace) and create a new SAML 2.0 application. Use the SP details from the Configure tab.'
                  },
                  {
                    step: '2',
                    title: 'Copy the IdP metadata',
                    body: 'After creating the SAML app, copy the SSO URL and download the X.509 signing certificate from your IdP.'
                  },
                  {
                    step: '3',
                    title: 'Enter configuration',
                    body: 'Paste the SSO URL and certificate into the Configure tab, then click "Save Configuration".'
                  },
                  {
                    step: '4',
                    title: 'Assign users',
                    body: 'In your IdP, assign users or groups to the Zentry GRC SAML application. They can then use SSO to log in.'
                  },
                  {
                    step: '5',
                    title: 'Test the connection',
                    body: 'Click "Test SSO Login" to verify the integration works correctly before rolling it out to your team.'
                  },
                ].map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">{item.title}</h4>
                      <p className="text-sm text-slate-500 mt-1">{item.body}</p>
                    </div>
                  </div>
                ))}

                <div className="pt-4 border-t border-slate-100">
                  <h4 className="font-medium text-slate-900 mb-3">Supported Identity Providers</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {['Okta', 'Azure AD / Entra', 'OneLogin', 'Google Workspace'].map((idp) => (
                      <div key={idp} className="bg-slate-50 rounded-md p-3 text-center text-sm font-medium text-slate-700">
                        {idp}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
